import React from "react";
import "./EmailPage.css";
import api from "../../apis/selectree_api";
import { toast } from "react-toastify";
import { uploadImageToS3 } from "../../actions/s3Upload";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaListOl,
  FaListUl,
  FaLink,
  FaCheckCircle,
  FaTimesCircle,
  FaImage,
  FaEraser,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaTextHeight,
} from "react-icons/fa";

const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

class EmailPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      subject: "",
      recipient: "",
      status: "",
      recipients: "all",
      users: [],
      loading: false,
      emailResult: null,
    };
    this.editorRef = React.createRef();
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this.getAllUsers();
    this.setupDragAndDrop();
  }

  componentWillUnmount() {
    this.removeDragAndDropListeners();
  }

  setupDragAndDrop = () => {
    setTimeout(() => {
      if (this.editorRef.current) {
        const editor = this.editorRef.current;

        ["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
          editor.addEventListener(eventName, this.preventDefaults, false);
        });

        ["dragenter", "dragover"].forEach(eventName => {
          editor.addEventListener(eventName, this.highlight, false);
        });

        ["dragleave", "drop"].forEach(eventName => {
          editor.addEventListener(eventName, this.unhighlight, false);
        });

        editor.addEventListener("drop", this.handleDrop, false);
      }
    }, 100);
  };

  removeDragAndDropListeners = () => {
    if (this.editorRef.current) {
      const editor = this.editorRef.current;
      ["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
        editor.removeEventListener(eventName, this.preventDefaults, false);
        editor.removeEventListener(eventName, this.highlight, false);
        editor.removeEventListener(eventName, this.unhighlight, false);
        editor.removeEventListener(eventName, this.handleDrop, false);
      });
    }
  };

  preventDefaults = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  highlight = () => {
    if (this.editorRef.current) {
      this.editorRef.current.style.backgroundColor = "#f0f8ff";
      this.editorRef.current.style.border = "2px dashed #007cba";
    }
  };

  unhighlight = () => {
    if (this.editorRef.current) {
      this.editorRef.current.style.backgroundColor = "";
      this.editorRef.current.style.border = "";
    }
  };

  handleDrop = e => {
    const dt = e.dataTransfer;
    const files = dt.files;

    this.processDroppedFiles(files);
  };

  getAllUsers = async () => {
    this.setState({ loading: true });
    try {
      let res = await api.get(`/users/email-users`, getAuthHeader());
      console.log("All users:", res.data);
      this.setState({ users: res.data, loading: false });
    } catch (err) {
      console.log(`Error fetching users: ${err}`);
      this.setState({
        status: "Error: Failed to load users",
        loading: false,
      });
    }
  };

  getTestUsers = async () => {
    this.setState({ loading: true });
    try {
      let res = await api.get(`/users/email-test-users`, getAuthHeader());
      console.log("Test users:", res.data);
      this.setState({ loading: false });
      return res.data;
    } catch (err) {
      console.log(`Error fetching test users: ${err}`);
      this.setState({
        status: "Error: Failed to load test users",
        loading: false,
      });
      return [];
    }
  };

  handleChange = e => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleRecipientChange = e => {
    this.setState({ recipients: e.target.value });
  };

  execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    this.editorRef.current.focus();
  };

  handleFontSizeChange = e => {
    const fontSize = e.target.value;
    this.execCommand("fontSize", fontSize);
  };

  handleAddLink = () => {
    const url = prompt(
      "Enter the URL for the link (must start with https://):"
    );
    if (url) {
      if (/^https:\/\//i.test(url)) {
        this.execCommand("createLink", url);
      } else {
        alert("Invalid URL. Please ensure it starts with https://");
      }
    }
  };

  handleImageUpload = event => {
    const files = Array.from(event.target.files);

    files.forEach(file => {
      this.processImageFile(file);
    });

    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
    }
  };

  processImageFile = file => {
    const maxSize = 50 * 1024 * 1024; // 50MB limit
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error(`${file.name} is not a supported image format`);
      return;
    }

    if (file.size > maxSize) {
      toast.error(`${file.name} is too large. Maximum size is 50MB`);
      return;
    }

    this.uploadImageToS3AndInsert(file);
  };

  processDroppedFiles = files => {
    Array.from(files).forEach(file => {
      this.processImageFile(file);
    });
  };

  handlePaste = async e => {
    const clipboardData = e.clipboardData || window.clipboardData;
    if (clipboardData.items) {
      for (let i = 0; i < clipboardData.items.length; i++) {
        const item = clipboardData.items[i];
        if (item.type.indexOf("image") !== -1) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            toast.info(`Uploading pasted image: ${file.name || "image"}`);
            await this.uploadImageToS3AndInsert(file);
          }
        }
      }
    }
  };

  uploadImageToS3AndInsert = async file => {
    try {
      toast.info(`Uploading ${file.name} to S3...`);
      const result = await uploadImageToS3(file, "email-images");

      if (result.success) {
        // insert the S3 image URL instead of base64
        this.insertImageFromS3(result.data.imageUrl, file.name);
        toast.success(`${file.name} uploaded successfully!`);
      } else {
        toast.error(`Failed to upload ${file.name}: ${result.message}`);
      }
    } catch (error) {
      console.error("Error uploading image to S3:", error);
      toast.error(`Failed to upload ${file.name}: ${error.message}`);
      // fallback to base64 if S3 upload fails
      this.compressAndInsertImage(file);
    }
  };

  compressAndInsertImage = file => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      // calculate new dimensions
      let { width, height } = img;
      const maxDimension = 1200;

      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        } else {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
      }

      canvas.width = width;
      canvas.height = height;

      ctx.drawImage(img, 0, 0, width, height);

      const quality = file.type === "image/png" ? 0.8 : 0.7;
      const compressedDataUrl = canvas.toDataURL(file.type, quality);

      this.insertImageDirectly(compressedDataUrl, file.name);
    };

    img.src = URL.createObjectURL(file);
  };

  insertImageFromS3 = (imageUrl, fileName) => {
    const imgHtml = `<div style="text-align: left;"><img src="${imageUrl}" alt="${fileName}" style="max-width: 100%; height: auto; margin: 10px 0;" /></div>`;

    if (this.editorRef.current) {
      const selection = window.getSelection();

      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        if (this.editorRef.current.contains(range.commonAncestorContainer)) {
          range.deleteContents();
          const div = document.createElement("div");
          div.innerHTML = imgHtml;
          range.insertNode(div.firstChild);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          this.editorRef.current.insertAdjacentHTML("beforeend", imgHtml);
        }
      } else {
        this.editorRef.current.insertAdjacentHTML("beforeend", imgHtml);
      }

      this.editorRef.current.focus();
    }
  };

  insertImageDirectly = (dataUrl, fileName) => {
    const imgHtml = `<div style="text-align: left;"><img src="${dataUrl}" alt="${fileName}" style="max-width: 100%; height: auto; margin: 10px 0;" /></div>`;

    if (this.editorRef.current) {
      const selection = window.getSelection();

      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        if (this.editorRef.current.contains(range.commonAncestorContainer)) {
          range.deleteContents();
          const div = document.createElement("div");
          div.innerHTML = imgHtml;
          range.insertNode(div.firstChild);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          this.editorRef.current.insertAdjacentHTML("beforeend", imgHtml);
        }
      } else {
        this.editorRef.current.insertAdjacentHTML("beforeend", imgHtml);
      }

      this.editorRef.current.focus();
    }
  };

  clearFormatting = () => {
    if (this.editorRef.current) {
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);

      if (range && !range.collapsed) {
        this.execCommand("removeFormat");
        this.execCommand("unlink");
      } else {
        const content =
          this.editorRef.current.textContent ||
          this.editorRef.current.innerText;
        this.editorRef.current.innerHTML = content;
      }

      this.editorRef.current.focus();
    }
  };

  prepareEmailData = (users, subject, htmlContent, textContent) => {
    return {
      users: users,
      subject: subject,
      textContent: textContent,
      htmlContent: htmlContent,
    };
  };

  sendEmail = async emailData => {
    try {
      console.log("Sending email:", emailData);

      const response = await api.post(
        "/users/email",
        emailData,
        getAuthHeader()
      );
      return response.data;
    } catch (err) {
      console.log(`Error sending email: ${err}`);
      throw new Error(err.response?.data?.message || "Failed to send email");
    }
  };

  handleSubmit = async e => {
    if (e) e.preventDefault();

    this.setState({ loading: true, emailResult: null });

    try {
      if (!this.state.subject) {
        this.setState({
          status: "Error: Subject is required",
          loading: false,
        });
        return;
      }

      if (!this.editorRef.current || !this.editorRef.current.innerHTML.trim()) {
        this.setState({
          status: "Error: Email content is required",
          loading: false,
        });
        return;
      }

      const htmlContent = this.editorRef.current.innerHTML;

      if (this.state.recipients === "single" && !this.state.recipient) {
        this.setState({
          status: "Error: Recipient email is required",
          loading: false,
        });
        return;
      }

      let users = [];

      if (this.state.recipients === "all") {
        users = this.state.users;
      } else if (this.state.recipients === "test") {
        users = await this.getTestUsers();
      } else {
        users = [{ email: this.state.recipient, email_notifications: true }];
      }

      if (!users || users.length === 0) {
        this.setState({
          status: "Error: No valid recipients found",
          loading: false,
        });
        return;
      }

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;
      const textContent = tempDiv.textContent || tempDiv.innerText || "";

      const emailData = this.prepareEmailData(
        users,
        this.state.subject,
        htmlContent,
        textContent
      );
      const result = await this.sendEmail(emailData);

      this.setState({
        status: `Email operation complete`,
        emailResult: result,
        subject: "",
        recipient: "",
        loading: false,
      });

      if (this.editorRef.current) {
        this.editorRef.current.innerHTML = "";
      }

      if (result.success) {
        toast.success(result.message || "Email sent successfully!");
      } else {
        toast.error(result.message || "Some emails failed to send");
      }
    } catch (err) {
      this.setState({
        status: `Error: ${err.message || "Failed to send email"}`,
        loading: false,
        emailResult: null,
      });
      toast.error("Failed to send email");
    }
  };

  clearForm = () => {
    this.setState({
      subject: "",
      recipient: "",
      emailResult: null,
      status: "",
    });

    if (this.editorRef.current) {
      this.editorRef.current.innerHTML = "";
    }
  };

  render() {
    const { subject, recipient, status, recipients, loading, emailResult } =
      this.state;

    return (
      <div className="email-container">
        <h1>Send Email</h1>

        {status && !emailResult && (
          <div className={status.includes("Error") ? "error" : "success"}>
            {status}
          </div>
        )}

        {emailResult && (
          <div className="email-result">
            <h2>Email Results</h2>
            <div className="result-summary">
              <p>
                <strong>Status: </strong>
                <span
                  className={
                    emailResult.success ? "success-text" : "error-text"
                  }
                >
                  {emailResult.success ? "Success" : "Failed"}
                </span>
              </p>
              <p>
                <strong>Message:</strong> {emailResult.message}
              </p>
              <p>
                <strong>Total Sent:</strong> {emailResult.totalSent} of{" "}
                {emailResult.details?.length || 0} emails
              </p>

              {emailResult.failures && emailResult.failures.length > 0 && (
                <p>
                  <strong>Failures:</strong> {emailResult.failures.length}{" "}
                  emails failed
                </p>
              )}
            </div>

            {emailResult.details && emailResult.details.length > 0 && (
              <div className="result-details">
                <h3>Delivery Details</h3>
                <table className="result-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Email</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailResult.details.map((detail, index) => (
                      <tr key={index}>
                        <td>
                          {detail.status === "sent" ? (
                            <FaCheckCircle className="success-icon" />
                          ) : (
                            <FaTimesCircle className="error-icon" />
                          )}
                          {detail.status}
                        </td>
                        <td>{detail.email}</td>
                        <td>{detail.error || detail.reason || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button
              type="button"
              onClick={this.clearForm}
              className="primary-button clear-results-button"
            >
              Clear Results & Start New Email
            </button>
          </div>
        )}

        {!emailResult && (
          <form onSubmit={this.handleSubmit}>
            <div className="form-group">
              <label>Recipients:</label>
              <select
                name="recipients"
                value={recipients}
                onChange={this.handleRecipientChange}
                className="recipient-select"
              >
                <option value="all">All Users</option>
                <option value="test">Test Group</option>
                <option value="single">Single Recipient</option>
              </select>
            </div>

            {recipients === "single" && (
              <div className="form-group">
                <label>Recipient Email:</label>
                <input
                  type="email"
                  name="recipient"
                  value={recipient}
                  onChange={this.handleChange}
                  placeholder="Enter email address"
                />
              </div>
            )}

            <div className="form-group">
              <label>Subject:</label>
              <input
                type="text"
                name="subject"
                value={subject}
                onChange={this.handleChange}
                placeholder="Enter email subject"
              />
            </div>

            <div className="form-group">
              <div className="editor-toolbar">
                <button
                  type="button"
                  onClick={() => this.execCommand("bold")}
                  title="Bold"
                >
                  <FaBold />
                </button>
                <button
                  type="button"
                  onClick={() => this.execCommand("italic")}
                  title="Italic"
                >
                  <FaItalic />
                </button>
                <button
                  type="button"
                  onClick={() => this.execCommand("underline")}
                  title="Underline"
                >
                  <FaUnderline />
                </button>
                <div className="toolbar-separator"></div>
                <div
                  className="font-size-container"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    margin: "0 5px",
                  }}
                >
                  <FaTextHeight style={{ marginRight: "5px", color: "#666" }} />
                  <select
                    onChange={this.handleFontSizeChange}
                    defaultValue=""
                    className="font-size-dropdown"
                    title="Font Size"
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "3px",
                      padding: "2px 5px",
                    }}
                  >
                    <option value="1">8pt</option>
                    <option value="2">10pt</option>
                    <option value="3">12pt</option>
                    <option value="4">14pt</option>
                    <option value="5">18pt</option>
                    <option value="6">24pt</option>
                    <option value="7">36pt</option>
                  </select>
                </div>
                <div className="toolbar-separator"></div>
                <button
                  type="button"
                  onClick={() => this.execCommand("insertOrderedList")}
                  title="Ordered List"
                >
                  <FaListOl />
                </button>
                <button
                  type="button"
                  onClick={() => this.execCommand("insertUnorderedList")}
                  title="Unordered List"
                >
                  <FaListUl />
                </button>
                <button
                  type="button"
                  onClick={() => this.handleAddLink()}
                  title="Add Link"
                >
                  <FaLink />
                </button>
                <div className="toolbar-separator"></div>
                <button
                  type="button"
                  onClick={() => this.execCommand("justifyLeft")}
                  title="Align Left"
                >
                  <FaAlignLeft />
                </button>
                <button
                  type="button"
                  onClick={() => this.execCommand("justifyCenter")}
                  title="Align Center"
                >
                  <FaAlignCenter />
                </button>
                <button
                  type="button"
                  onClick={() => this.execCommand("justifyRight")}
                  title="Align Right"
                >
                  <FaAlignRight />
                </button>
                <div className="toolbar-separator"></div>
                <button
                  type="button"
                  onClick={() => this.fileInputRef.current?.click()}
                  title="Add Image"
                  className="image-upload-button"
                >
                  <FaImage />
                </button>
                <button
                  type="button"
                  onClick={this.clearFormatting}
                  title="Clear Formatting"
                  className="clear-formatting-button"
                >
                  <FaEraser />
                </button>
                <input
                  ref={this.fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  multiple
                  onChange={this.handleImageUpload}
                  style={{ display: "none" }}
                  title="Upload images (up to 50MB each)"
                />
              </div>
              <div className="editor-instructions">
                <small style={{ color: "#666", fontStyle: "italic" }}>
                  💡 <strong>Note:</strong> You can paste images directly from
                  your clipboard (Ctrl+V/Cmd+V), drag & drop images, or use the
                  upload button above. Images are automatically uploaded to AWS.
                </small>
              </div>
              <div
                ref={this.editorRef}
                className="rich-text-editor"
                contentEditable={true}
                suppressContentEditableWarning={true}
                style={{
                  minHeight: "200px",
                  border: "1px solid #ccc",
                  padding: "10px",
                  borderRadius: "4px",
                  transition: "all 0.3s ease",
                }}
                placeholder="Type your email content here, paste images, or drag and drop images..."
                onPaste={this.handlePaste}
              />
            </div>

            <div className="button-group">
              <button
                type="submit"
                disabled={loading}
                className="primary-button"
              >
                {loading ? "Sending..." : "Send Email"}
              </button>
              <button
                type="button"
                onClick={this.clearForm}
                disabled={loading}
                className="secondary-button"
              >
                Clear
              </button>
            </div>
          </form>
        )}
      </div>
    );
  }
}

export default EmailPage;
